import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faSearch,
  faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export type DSDropdownSize = "sm" | "md";

export interface DSDropdownOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  isHeader?: boolean;
  icon?: IconProp;
}

export interface DSDropdownProps<T = DSDropdownOption> {
  options: T[];
  value?: string | number | null;
  onChange?: (value: string | number, option: T) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  search?: boolean;
  leadingIcon?: IconProp;
  disabled?: boolean;
  width?: string;
  className?: string;
  dataTestId: string;
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string | number;
  isOptionDisabled?: (option: T) => boolean;
  noResultsMessage?: string;
  ref?: React.Ref<HTMLDivElement>;
  size?: DSDropdownSize;
  onOpenChange?: (isOpen: boolean) => void;
  renderOption?: (option: T, index: number) => React.ReactNode;
  getOptionTestId?: (option: T, index: number) => string;
  renderButton?: (selectedOption: T | null) => React.ReactNode;
  afterOptions?: React.ReactNode;
  maxHeight?: number;
  label?: string;
}

function DSDropdownInner<T = DSDropdownOption>({
  options = [],
  value,
  onChange,
  placeholder = "Option",
  searchPlaceholder = "Search...",
  search = false,
  leadingIcon,
  disabled = false,
  width = "w-72",
  className = "",
  dataTestId,
  getOptionLabel = (opt) => (opt as DSDropdownOption).label,
  getOptionValue = (opt) => (opt as DSDropdownOption).value,
  isOptionDisabled = (opt) =>
    (opt as DSDropdownOption).disabled ||
    (opt as DSDropdownOption).isHeader ||
    false,
  noResultsMessage = "No results found",
  ref,
  size = "md",
  onOpenChange,
  renderOption,
  getOptionTestId,
  renderButton,
  afterOptions,
  maxHeight,
  label,
}: DSDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  const onIsOpenChange = useEffectEvent(() => {
    onOpenChange?.(isOpen);
  });

  useEffect(() => {
    onIsOpenChange();
  }, [isOpen]);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const listboxId = useId();
  const openedViaKeyboardRef = useRef(false);
  const internalRef = useRef<HTMLDivElement>(null);
  const dropdownRef =
    (ref as React.RefObject<HTMLDivElement>) || internalRef;
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Find selected option
  const selectedOption =
    options.find((opt) => getOptionValue(opt) === value) || null;

  // Filter options based on search term
  const filteredOptions = (() => {
    if (!searchTerm) {
      return options;
    }
    const loweredTerm = searchTerm.toLowerCase();
    return options.filter((option) => {
      const labelValue = getOptionLabel(option);
      return labelValue
        ?.toString()
        ?.toLowerCase()
        ?.includes(loweredTerm);
    });
  })();

  // Display label
  const displayLabel = selectedOption
    ? getOptionLabel(selectedOption)
    : placeholder;

  // Update position on open and on scroll/resize
  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!dropdownRef.current) return;
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, dropdownRef, maxHeight, afterOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInTrigger =
        dropdownRef.current?.contains(target) ?? false;
      const clickedInMenu =
        menuRef.current?.contains(target) ?? false;

      if (!clickedInTrigger && !clickedInMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      if (search) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, search, dropdownRef]);

  // Reset search when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Stable callback that always reads fresh values
  const resetHighlight = useEffectEvent(() => {
    const selectedIndex = filteredOptions.findIndex(
      (opt) => getOptionValue(opt) === value,
    );
    if (
      selectedIndex >= 0 &&
      !isOptionDisabled(filteredOptions[selectedIndex])
    ) {
      setHighlightedIndex(selectedIndex);
    } else {
      const firstEnabledIndex = filteredOptions.findIndex(
        (opt) => !isOptionDisabled(opt),
      );
      setHighlightedIndex(
        firstEnabledIndex >= 0 ? firstEnabledIndex : -1,
      );
    }
  });

  // Reset highlight when search term changes or dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    resetHighlight();
  }, [isOpen, searchTerm]);

  // Handle option selection
  const selectOption = (option: T) => {
    if (isOptionDisabled(option)) {
      return;
    }
    const optionValue = getOptionValue(option);
    onChange?.(optionValue, option);
    setIsOpen(false);
  };

  // Scroll highlighted option into view when navigating
  useEffect(() => {
    if (
      isOpen &&
      highlightedIndex >= 0 &&
      optionRefs.current[highlightedIndex]
    ) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [isOpen, highlightedIndex]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle opening when closed
    if (!isOpen) {
      if (
        e.key === "Enter" ||
        e.key === " " ||
        e.key === "ArrowDown"
      ) {
        e.preventDefault();
        openedViaKeyboardRef.current = true;
        setIsOpen(true);
      }
      return;
    }

    const isArrowDown =
      e.key === "ArrowDown" || e.code === "ArrowDown";
    const isArrowUp = e.key === "ArrowUp" || e.code === "ArrowUp";

    // Prevent arrow keys from scrolling when dropdown is open
    if (isArrowDown || isArrowUp) {
      e.preventDefault();
      e.stopPropagation();

      if (isArrowDown) {
        setHighlightedIndex((prev) => {
          let next = prev + 1;
          while (
            next < filteredOptions.length &&
            isOptionDisabled(filteredOptions[next])
          ) {
            next += 1;
          }
          return next < filteredOptions.length ? next : prev;
        });
      } else {
        setHighlightedIndex((prev) => {
          let next = prev - 1;
          while (
            next >= 0 &&
            isOptionDisabled(filteredOptions[next])
          ) {
            next -= 1;
          }
          return next >= 0 ? next : prev;
        });
      }
      return;
    }

    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          const candidate = filteredOptions[highlightedIndex];
          if (!isOptionDisabled(candidate)) {
            selectOption(candidate);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      case "Home":
        e.preventDefault();
        {
          const firstEnabled = filteredOptions.findIndex(
            (opt) => !isOptionDisabled(opt),
          );
          if (firstEnabled >= 0) setHighlightedIndex(firstEnabled);
        }
        break;
      case "End":
        e.preventDefault();
        {
          let lastEnabled = -1;
          for (let i = filteredOptions.length - 1; i >= 0; i--) {
            if (!isOptionDisabled(filteredOptions[i])) {
              lastEnabled = i;
              break;
            }
          }
          if (lastEnabled >= 0) setHighlightedIndex(lastEnabled);
        }
        break;
      default:
        break;
    }
  };

  // Styles

  const sizeStyles = {
    sm: "h-8 px-sm py-xs",
    md: "h-10 px-md py-sm",
  };
  const triggerBaseStyles =
    "flex items-center justify-between gap-xs rounded-standard transition-all cursor-pointer bg-input";

  const triggerStateStyles = disabled
    ? "opacity-80 shadow-[0px_0px_10px_0px_var(--shadow-secondary)] cursor-not-allowed"
    : isOpen
      ? "border-2 border-brand shadow-[0px_0px_10px_0px_var(--shadow-focus)]"
      : "shadow-[0px_0px_10px_0px_var(--shadow-primary)] hover:shadow-[0px_0px_10px_0px_var(--shadow-focus)]";

  return (
    <div
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      className={`relative ${width} ${className}`}
    >
      {label && (
        <label className="text-xs text-secondary mb-xs block">
          {label}
        </label>
      )}
      {/* Trigger Button / Search Input */}
      {isOpen && search ? (
        <div
          className={`${triggerBaseStyles} ${sizeStyles[size]} ${triggerStateStyles} w-full overflow-hidden`}
        >
          <FontAwesomeIcon
            icon={faSearch}
            className="text-brand text-base shrink-0"
            aria-hidden="true"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 min-w-0 bg-transparent text-sm text-primary placeholder:text-placeholder outline-none"
            data-testid={dataTestId}
            data-intercom-target={dataTestId}
          />
          <button
            type="button"
            onClick={() => {
              if (searchTerm) {
                setSearchTerm("");
                searchInputRef.current?.focus();
              } else {
                setIsOpen(false);
              }
            }}
            className="text-brand hover:text-indigo-700 transition-colors shrink-0"
          >
            <FontAwesomeIcon
              icon={faTimes}
              size="1x"
              className="text-xs"
            />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            if (disabled) return;
            if (openedViaKeyboardRef.current) {
              openedViaKeyboardRef.current = false;
              return;
            }
            setIsOpen(!isOpen);
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          data-testid={dataTestId}
          className={`${triggerBaseStyles} ${sizeStyles[size]} ${triggerStateStyles} w-full text-left`}
        >
          {leadingIcon && (
            <FontAwesomeIcon
              icon={leadingIcon}
              className={
                disabled
                  ? "text-placeholder text-base"
                  : "text-brand text-base"
              }
            />
          )}
          {renderButton ? (
            renderButton(selectedOption)
          ) : (
            <span
              className={`flex-1 min-w-0 truncate text-sm ${
                disabled ? "text-placeholder" : "text-primary"
              }`}
            >
              {displayLabel}
            </span>
          )}
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className={
              disabled
                ? "text-placeholder text-xs"
                : "text-brand text-xs"
            }
          />
        </button>
      )}

      {/* Dropdown Menu - Portaled to body for proper z-index layering */}
      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed bg-input rounded-standard shadow-[0px_0px_10px_0px_var(--shadow-primary)] overflow-hidden z-dropdown"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              minWidth: menuPosition.width,
              maxWidth: menuPosition.width,
            }}
          >
            <ul
              id={listboxId}
              role="listbox"
              aria-label={placeholder}
              className="overflow-y-auto m-0 p-0"
              style={{
                maxHeight: maxHeight ?? 176,
              }}
            >
              {filteredOptions.length === 0 ? (
                <li className="px-md py-sm text-sm text-secondary text-center">
                  {noResultsMessage}
                </li>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionValue = getOptionValue(option);
                  const isSelected = optionValue === value;
                  const isHighlighted = index === highlightedIndex;
                  const isDisabled = isOptionDisabled(option);
                  const isHeader =
                    (option as DSDropdownOption).isHeader || false;
                  const headerIcon =
                    (option as DSDropdownOption).icon || null;

                  const optionTestId = getOptionTestId?.(
                    option,
                    index,
                  );

                  // Render section header
                  if (isHeader) {
                    return (
                      <li
                        key={`${optionValue}-${index}`}
                        ref={(el) => {
                          optionRefs.current[index] = el;
                        }}
                        role="presentation"
                        data-testid={optionTestId}
                        className={`${sizeStyles[size]} text-xs font-semibold bg-brand-subtle text-primary border-b-2 border-brand flex items-center gap-xs cursor-default`}
                      >
                        {headerIcon && (
                          <FontAwesomeIcon
                            icon={headerIcon}
                            className="text-xs shrink-0"
                          />
                        )}
                        <span className="min-w-0 truncate">
                          {getOptionLabel(option)}
                        </span>
                      </li>
                    );
                  }

                  // Render regular option
                  return (
                    <li
                      key={`${optionValue}-${index}`}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={isDisabled}
                      data-testid={optionTestId}
                      onClick={() =>
                        !isDisabled && selectOption(option)
                      }
                      onMouseEnter={() =>
                        !isDisabled && setHighlightedIndex(index)
                      }
                      className={`${sizeStyles[size]} text-sm cursor-pointer transition-colors flex items-center justify-between gap-sm ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : isHighlighted
                            ? "bg-tertiary"
                            : "bg-input hover:bg-tertiary"
                      } ${
                        isSelected ? "font-medium" : ""
                      } text-primary`}
                    >
                      {renderOption ? (
                        renderOption(option, index)
                      ) : (
                        <span className="min-w-0 truncate">
                          {getOptionLabel(option)}
                        </span>
                      )}
                      {isSelected && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-brand text-xs shrink-0"
                        />
                      )}
                    </li>
                  );
                })
              )}
            </ul>
            {afterOptions && (
              <div onClick={() => setIsOpen(false)}>
                {afterOptions}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

export const DSDropdown = DSDropdownInner as (<T = DSDropdownOption>(
  props: DSDropdownProps<T>,
) => React.ReactElement) & {
  displayName?: string;
};

DSDropdown.displayName = "DSDropdown";
